import { useContext, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  BookOpen,
  ChevronLeft,
  ChevronsLeftRight,
  CircleHelp,
  Coins,
  FileBox,
  Gauge,
  Home,
  KeyRound,
  LayoutDashboard,
  Receipt,
  Repeat2,
  Sparkles,
  Tags,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import { TitleContext } from "../../contexts/TitleContext";
import { SideMenuContext } from "../../contexts/SideMenuContext";
import { TOASTPROPS } from "../../shared/Constants";
import HelpForm from "../HelpForm";
import symbol from "../../assets/brand/maritaca-symbol.svg";
import symbolDark from "../../assets/brand/maritaca-symbol-dark.svg";
import wordmark from "../../assets/brand/maritaca-lettering.svg";
import wordmarkDark from "../../assets/brand/maritaca-lettering-dark.svg";
import { ThemeContext } from "../../contexts/ThemeContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import UserMenu from "./UserMenu";

type Item = { to: string; title: string; icon: React.ComponentType<{ className?: string }> };
type Section = { label: string; items: Item[] };

const sections: Section[] = [
  { label: "Workspace", items: [
    { to: "/home", title: "Início", icon: Home },
    { to: "/gastos", title: "Consumo", icon: LayoutDashboard },
    { to: "/chaves-de-api", title: "Chaves de API", icon: KeyRound },
    { to: "/playground", title: "Playground", icon: ChevronsLeftRight },
  ]},
  { label: "Desenvolver", items: [
    { to: "/modelos", title: "Modelos", icon: Sparkles },
    { to: "/precos", title: "Preços", icon: Tags },
    { to: "/batch", title: "Batch API", icon: Repeat2 },
    { to: "/arquivos", title: "Arquivos", icon: FileBox },
    { to: "/rate-limits", title: "Rate Limits", icon: Gauge },
  ]},
  { label: "Gerenciar", items: [
    { to: "/recarga", title: "Recarga", icon: Coins },
    { to: "/comprovantes", title: "Comprovantes", icon: Receipt },
    { to: "/organizacoes", title: "Organizações", icon: Users },
  ]},
];

const titleByPath: Record<string, string> = {
  "/chaves-de-api": "Chaves de API",
  "/organizacoes": "Organizações",
  "/recarga": "Recarga",
  "/comprovantes": "Comprovantes",
  "/modelos": "Modelos",
  "/precos": "Preços",
  "/rate-limits": "Rate Limits",
  "/gastos": "Gastos",
  "/": "Plataforma",
  "/home": "Plataforma",
  "/batch": "Batch API",
  "/arquivos": "Arquivos",
  "/playground": "Playground",
};

function NavItem({
  item,
  active,
  collapsed,
  onClick,
}: {
  item: Item;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      aria-label={item.title}
      onClick={onClick}
      className={cn(
        "essential-nav-item group flex items-center rounded-md text-[13px] font-medium transition-colors",
        active
          ? "bg-brand-light/25 text-foreground dark:bg-white/[0.07]"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <span className="essential-nav-icon"><Icon
        className={cn(
          "h-[17px] w-[17px] shrink-0",
          active ? "text-foreground" : "text-muted-foreground/80 group-hover:text-foreground"
        )}
      /></span>
      <span className="essential-nav-label" aria-hidden={collapsed}>{item.title}</span>
    </NavLink>
  );
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      {collapsed && <TooltipContent side="right" sideOffset={10}>{item.title}</TooltipContent>}
    </Tooltip>
  );
}

export default function SideMenu() {
  const { pathname } = useLocation();
  const { title, setTitle } = useContext(TitleContext);
  const { isDarkMode } = useContext(ThemeContext);
  const { openHelp, setOpenHelp, toastMessage, isSideMenuCollapsed, setIsSideMenuCollapsed } =
    useContext(SideMenuContext);
  const collapsed = isSideMenuCollapsed;

  useEffect(() => {
    if (!toastMessage) return;
    const fn = (toast as any)[toastMessage.type] ?? toast.info;
    fn(toastMessage.message, TOASTPROPS);
  }, [toastMessage]);

  useEffect(() => {
    setTitle(titleByPath[pathname] ?? "Plataforma");
  }, [pathname, setTitle]);

  return (
    <TooltipProvider>
      {/* A fixed icon rail and persistent labels keep geometry stable during width changes. */}
      <aside
        data-testid="platform-sidebar"
        data-collapsed={collapsed}
        className={cn(
          "essential-navigation relative flex h-screen shrink-0 flex-col overflow-hidden border-r border-border bg-background",
          collapsed ? "w-[68px]" : "w-[208px]",
          "max-md:absolute max-md:z-40 max-md:h-screen",
          collapsed && "max-md:-translate-x-full"
        )}
      >
        <div className="essential-brand-header">
          <a href="https://maritaca.ai/" target="_blank" rel="noopener noreferrer" className="essential-brand-link">
            <img src={isDarkMode ? wordmarkDark : wordmark} alt="" aria-hidden="true" className="essential-brand-wordmark" />
            <img src={isDarkMode ? symbolDark : symbol} alt="Maritaca" className="essential-brand-symbol" />
          </a>
          <button
            onClick={() => setIsSideMenuCollapsed(!collapsed)}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            aria-expanded={!collapsed}
            className="essential-toggle-button"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col overflow-y-auto px-2.5 pb-2 [scrollbar-width:thin]">
          {sections.map((section) => (
            <div key={section.label} className="mb-3">
              <p className="essential-section-name mb-2 px-3 pt-2 text-[11px] font-normal text-muted-foreground" aria-hidden={collapsed}>
                {section.label}
              </p>
              <div className="space-y-px">
                {section.items.map((item) => (
                  <NavItem
                    key={item.to}
                    item={item}
                    active={title === titleByPath[item.to]}
                    collapsed={collapsed}
                    onClick={() => {
                      setTitle(titleByPath[item.to]);
                      if (window.innerWidth < 768) setIsSideMenuCollapsed(true);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className="mt-auto space-y-px border-t border-border pt-2">
            {[{label:"Ajuda", icon:CircleHelp, action:() => setOpenHelp(true)},
              {label:"Documentação", icon:BookOpen, action:() => window.open("https://docs.maritaca.ai", "_blank", "noopener,noreferrer")}
            ].map(({label, icon:Icon, action}) => (
              <Tooltip key={label} delayDuration={200}>
                <TooltipTrigger asChild>
                  <button aria-label={label} onClick={action} className="essential-nav-item flex w-full items-center rounded-md text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground">
                    <span className="essential-nav-icon"><Icon className="h-[17px] w-[17px]" /></span>
                    <span className="essential-nav-label" aria-hidden={collapsed}>{label}</span>
                  </button>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right" sideOffset={10}>{label}</TooltipContent>}
              </Tooltip>
            ))}
          </div>
        </nav>

        <div className="essential-account-footer border-t border-border">
          <UserMenu variant="sidebar" collapsed={collapsed} />
        </div>
      </aside>

      <Dialog open={openHelp} onOpenChange={(o) => !o && setOpenHelp(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajuda</DialogTitle>
          </DialogHeader>
          <HelpForm />
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
